/**
 * Sandbox storage by lifetime — Phase 5 (the layout migration).
 *
 * On its next wake a box moves its volume into the tidy layout of
 * `lifetimes.ts`: registered apps under `files/apps/<name>`, everything
 * unclassified quarantined under `.vestra/legacy/`. The runner's migrator
 * produces one of these reports per boot and posts it to the server, which
 * stores it on `user_sandbox_state.layout_migration` and repoints the app rows
 * so the Apps panel keeps working.
 *
 * The shape lives here because both ends need it: the runner builds it, the
 * server validates what arrives. It crosses the wire from a box, so the server
 * parses it rather than casting it.
 */
import { z } from 'zod';

/**
 * The layout a migrated box is at. `user_sandbox_state.layout_version` holds
 * this once a box has completed the move; NULL means pre-migration.
 */
export const LAYOUT_VERSION = 1;

/** Files moved, bytes freed — never negative, never fractional. */
const count = z.number().int().min(0);

/**
 * One step of the migration. `skipped` carries why a step did nothing (the
 * budget ran out, the source was absent) — a step that reports nothing and says
 * nothing about it is the one thing the staging dry-run day cannot read.
 */
export const MigrationStepSchema = z.object({
  name: z.string().min(1),
  moved: count,
  deleted: count,
  bytes: count,
  skipped: z.string().optional(),
});
export type MigrationStep = z.infer<typeof MigrationStepSchema>;

/**
 * A folder that moved. `port` is present only for a REGISTERED app — that is
 * what lets the server repoint `task_apps.cwd`; a quarantined folder has no
 * port and no row to fix.
 *
 * `fromRaw` is the app row's `cwd` EXACTLY as it is stored — free text a model
 * wrote, so `/ws/proj/` and `./proj` are both shapes it really takes — while
 * `from` is that text resolved. The server matches the row on either, because
 * matching only the resolved form matches nothing at all on a row whose `cwd`
 * carries a trailing slash, and the row then points at a folder that has moved
 * (A4). Absent on a quarantine, which has no row.
 *
 * `skipped` marks a relocation that did NOT happen: `to` equals `from` because
 * the folder stayed where it is, and the line says why (R121 — a second app
 * registered under a name this run already placed). The server never writes one;
 * it is here so the dry day surfaces the collision for a human.
 */
export const MigrationRelocationSchema = z.object({
  from: z.string().min(1),
  fromRaw: z.string().min(1).optional(),
  to: z.string().min(1),
  port: z.number().int().min(1).max(65535).optional(),
  skipped: z.string().optional(),
});
export type MigrationRelocation = z.infer<typeof MigrationRelocationSchema>;

/**
 * What one box did (or, in `dry` mode, would have done). `complete` is false
 * when the migrator ran out of its budget partway: the next boot resumes, so a
 * partial report is a legitimate record, not a failure.
 */
export const MigrationReportSchema = z.object({
  // The literal, not a number: a report claiming a version this code does not
  // know would stamp `layout_version` ahead of the program, and every later
  // reader would believe a box is at a layout nothing has ever produced.
  version: z.literal(LAYOUT_VERSION),
  // 'off' never runs the migrator, so it never produces a report.
  mode: z.enum(['dry', 'on']),
  steps: z.array(MigrationStepSchema),
  relocations: z.array(MigrationRelocationSchema),
  quarantined: z.array(z.string().min(1)),
  durationMs: count,
  complete: z.boolean(),
  // Written by the SERVER when it stores the report, never by the runner: how
  // many port-bearing relocations matched no app row. A folder that moved while
  // its row says otherwise is the one outcome this rail cannot repair by itself,
  // so the count is kept beside the report rather than only in a log line (A4).
  unmatched: count.optional(),
});
export type MigrationReport = z.infer<typeof MigrationReportSchema>;

/**
 * One registered app as the migrator needs it: where it is, what to call it, what it
 * serves. A schema and not a bare interface, for the same reason the report is one: this
 * list crosses the wire from the server to a box, and it is what decides which folders
 * that box may bury — so the box parses it rather than trusting its shape.
 *
 * `name` is NULLABLE (R146). An older registration carries a working folder and no name
 * at all, and such a row is STILL a registered app: there is no `files/apps/<name>` to
 * move it to, but a migrator that never hears about it reads the folder as an
 * unclassified root dump, quarantines it under `.vestra/legacy/`, and leaves the row
 * pointing at a path that is gone — the one mistake this migration must never make
 * (R121, R134). Null means "no name". `''` is not a name either and is refused here, so
 * a blank one is normalized to null where the row is read rather than arriving as a name
 * nothing can place.
 *
 * `port` is bounded to the TCP range exactly as a relocation's is: a row whose port the
 * report schema would refuse must never enter the report, because the server parses the
 * report WHOLE and one impossible port would cost that box every repoint in it.
 */
export const RegisteredAppSchema = z.object({
  folder: z.string().min(1),
  name: z.string().min(1).nullable(),
  port: z.number().int().min(1).max(65535),
});
export type RegisteredApp = z.infer<typeof RegisteredAppSchema>;
